import { deflateSync, inflateSync, unzlibSync } from 'fflate';
import type { Character } from '@/types/character';
import { CURRENT_SCHEMA_VERSION } from '@/types/character';

// ---- base64url ----
function base64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
function base64urlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---- share hash ----
export function encodeShareHash(c: Character): string {
  const json = JSON.stringify(c);
  const bytes = new TextEncoder().encode(json);
  const deflated = deflateSync(bytes, { level: 9 });
  return base64urlEncode(deflated);
}

export function decodeShareHash(hash: string): Character {
  const clean = hash.replace(/^#?(c=)?/, '');
  const bytes = base64urlDecode(clean);
  // Accept both raw-deflate (what encodeShareHash emits) and zlib-wrapped
  // deflate (what Node's `zlib.deflateSync` emits by default). AI-generated
  // URLs sometimes arrive in the zlib flavour; tolerating both avoids
  // silently rejecting otherwise-valid shared characters.
  const isZlib = bytes.length >= 2 && bytes[0] === 0x78;
  const inflated = isZlib ? unzlibSync(bytes) : inflateSync(bytes);
  const json = new TextDecoder().decode(inflated);
  const parsed = JSON.parse(json);
  return migrate(parsed);
}

// ---- file export/import ----
export function exportCharacterJson(c: Character): string {
  return JSON.stringify(c, null, 2);
}

export function importCharacterJson(text: string): Character {
  const parsed = JSON.parse(text);
  return migrate(parsed);
}

// ---- version migration ----
import { blankCharacter } from '@/types/character';
import type { CharacterType } from '@/types/character';

export function migrate(input: unknown): Character {
  if (!input || typeof input !== 'object') throw new Error('Not a character document');
  const raw = input as Record<string, unknown> & { v?: number };
  if (raw.v == null) throw new Error('Missing schema version (`v`)');
  if (raw.v !== CURRENT_SCHEMA_VERSION)
    throw new Error(`Unsupported schema version: v${raw.v}`);

  const type = (raw.type as CharacterType) ?? 'grog';
  // Shallow-merge against a fresh blank character so required fields
  // (characteristics, virtues, flaws, abilities, personality, reputations,
  // and post-r2 additions like wounds/fatigueLevel/size/warping) are
  // always present. Keeps old shared URLs readable.
  const filled = { ...blankCharacter(type), ...(raw as object) } as Character;
  if (type === 'magus') {
    filled.magus = { arts: [], spells: [], ...(raw.magus as object | undefined) };
  }
  if (type === 'companion') {
    filled.companion = { ...(raw.companion as object | undefined) };
  }
  if (type === 'grog') {
    filled.grog = { ...(raw.grog as object | undefined) };
  }
  return filled;
}

// ---- URL helpers ----
export function readCharacterFromLocation(loc: Location): Character | null {
  const hash = loc.hash;
  if (!hash || !hash.startsWith('#c=')) return null;
  try {
    return decodeShareHash(hash.slice(3));
  } catch (err) {
    console.warn('share hash decode failed:', err);
    return null;
  }
}

export function buildShareUrl(c: Character, base = window.location.origin + window.location.pathname): string {
  return `${base}#c=${encodeShareHash(c)}`;
}
